// src/pages/portal/Dashboard.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, getDocs, limit } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";

export default function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    
    async function load() {
      // 1. Fetch Orders Independently
      try {
        const ordSnap = await getDocs(
          query(collection(db, "orders"), where("uid", "==", currentUser.uid), orderBy("createdAt", "desc"), limit(3))
        );
        setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { 
        console.error("Failed to load orders. Check for missing Firebase Index:", e); 
      }

      // 2. Fetch Tickets Independently
      try {
        const tkSnap = await getDocs(
          query(collection(db, "tickets"), where("uid", "==", currentUser.uid), orderBy("createdAt", "desc"), limit(3))
        );
        setTickets(tkSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { 
        console.error("Failed to load tickets. Check for missing Firebase Index:", e); 
      }

      setLoading(false);
    }
    
    load();
  }, [currentUser]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const name = userProfile?.displayName || currentUser?.email?.split("@")[0] || "there";

  // Slice the data here so the bottom lists only show the 3 most recent
  const recentOrders = orders.slice(0, 3);
  const recentTickets = tickets.slice(0, 3);

  return (
    <div>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>
          {greeting()}, {name} 👋
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9375rem" }}>
          Here's an overview of your account and active services.
        </p>
      </motion.div>

      {/* Quick stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 40 }}>
        {[
          { label: "Active Orders", value: orders.filter(o => o.status === "active").length || "0", icon: "📦", color: "var(--accent)" },
          { label: "Open Tickets", value: tickets.filter(t => t.status !== "resolved").length || "0", icon: "🎫", color: "#60a5fa" },
          { label: "Account Type", value: userProfile?.role === "admin" ? "Admin" : "Client", icon: "👤", color: "#c084fc" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 20px" }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 800, fontFamily: "var(--font-head)", color: s.color }}>{s.value}</div>
              <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>{s.label}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick actions */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 16 }}>Quick actions</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          {[
            { label: "New Support Ticket", icon: "🎫", to: "/portal/support" },
            { label: "View My Orders", icon: "📦", to: "/portal/orders" },
            { label: "Pay an Invoice", icon: "💳", to: "/portal/billing" },
            { label: "Add a Service", icon: "🚀", to: "/portal/upgrades" },
            { label: "View Packages", icon: "📋", to: "/packages" },
            { label: "Book Appointment", icon: "📅", to: "https://calendar.app.google/y15Ur5NnKqAPpiNw8", external: true },
          ].map(({ label, icon, to, external }) => (
            external
              ? <a key={label} href={to} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--text)", fontSize: "0.875rem", fontWeight: 500, transition: "border-color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--border-med)"; e.currentTarget.style.background = "var(--surface-2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--surface)"; }}
                ><span>{icon}</span>{label}</a>
              : <Link key={label} to={to}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--text)", fontSize: "0.875rem", fontWeight: 500, transition: "border-color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--border-med)"; e.currentTarget.style.background = "var(--surface-2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--surface)"; }}
                ><span>{icon}</span>{label}</Link>
          ))}
        </div>
      </motion.div>

      {/* Recent orders */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} style={{ marginBottom: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>Recent Orders</h2>
          <Link to="/portal/orders" style={{ fontSize: "0.8125rem", color: "var(--accent)" }}>View all →</Link>
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}><div className="spinner" style={{ margin: "0 auto" }} /></div>
        ) : recentOrders.length === 0 ? (
          <EmptyState icon="📦" title="No orders yet" desc="Your orders will appear here once you purchase a package." cta={{ label: "View Packages", to: "/packages" }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {recentOrders.map(o => <OrderRow key={o.id} order={o} />)}
          </div>
        )}
      </motion.div>

      {/* Recent tickets */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>Recent Support Tickets</h2>
          <Link to="/portal/support" style={{ fontSize: "0.8125rem", color: "var(--accent)" }}>View all →</Link>
        </div>
        {loading ? null : recentTickets.length === 0 ? (
          <EmptyState icon="🎫" title="No tickets yet" desc="Submit a support request when you need help with your services." cta={{ label: "New Ticket", to: "/portal/support" }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {recentTickets.map(t => <TicketRow key={t.id} ticket={t} />)}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function OrderRow({ order }) {
  const statusColors = { active: "badge-accent", pending: "badge-amber", completed: "badge-blue", cancelled: "badge-red" };
  
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.9375rem", color: "var(--text)", marginBottom: 4 }}>
          {order.packageName || "Custom Package"}
        </div>
        <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
          {order.createdAt?.toDate?.()?.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) || "—"}
        </div>
      </div>
      
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {order.amount && <div style={{ fontWeight: 600 }}>₱{order.amount.toLocaleString()}</div>}
        <span className={`badge ${statusColors[order.status] || "badge-blue"}`}>
          {order.status || "active"}
        </span>
      </div>
    </div>
  );
}

function TicketRow({ ticket }) {
  const statusColors = { open: "badge-amber", "in-progress": "badge-blue", resolved: "badge-accent" };
  
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px" }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: "0.9375rem", marginBottom: 4 }}>
          {ticket.subject || "Support Request"}
        </div>
        <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
          {ticket.createdAt?.toDate?.()?.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) || "—"}
        </div>
      </div>
      <span className={`badge ${statusColors[ticket.status] || "badge-amber"}`}>
        {ticket.status || "open"}
      </span>
    </div>
  );
}

function EmptyState({ icon, title, desc, cta }) { 
  return ( 
    <div style={{ padding: "40px 24px", textAlign: "center", background: "var(--surface)", border: "1px dashed var(--border-med)", borderRadius: 12 }}> 
      <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div> 
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div> 
      <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 16 }}>{desc}</p> 
      {cta && (
        <Link to={cta.to} className="btn btn-primary">
          {cta.label}
        </Link>
      )} 
    </div>
  ); 
}